/**
 * Icon Resolver Utility
 * Resolves icon names to their components (Lucide, Simple Icons, Microsoft Icons, or Integration Icons)
 */

import { type LucideIcon, AlertCircle } from 'lucide-react';
import * as SimpleIcons from '@icons-pack/react-simple-icons';
import { getMicrosoftIcon, hasMicrosoftIcon } from './microsoftIcons';
import { getIntegrationIcon, hasIntegrationIcon } from './integrationIcons';

export type IconType = 'lucide' | 'simple-icon' | 'microsoft-icon' | 'integration-icon';

export interface ResolvedIcon {
  type: IconType;
  component: LucideIcon | React.ComponentType<any>;
  color?: string;
}

/**
 * Resolves an icon name to its component
 * @param iconName - Name of the icon (e.g., 'siSlack', 'Globe')
 * @param iconType - Type of icon system to use
 * @returns Resolved icon with component and metadata
 */
export function resolveIcon(
  iconName: string,
  iconType: IconType = 'lucide',
  lucideIconMap?: Record<string, LucideIcon>
): ResolvedIcon {
  // Handle integration icons (custom SVG icons for third-party services)
  if (iconType === 'integration-icon') {
    const IntegrationIcon = getIntegrationIcon(iconName);
    if (IntegrationIcon) {
      return {
        type: 'integration-icon',
        component: IntegrationIcon,
      };
    }
    return {
      type: 'lucide',
      component: AlertCircle,
    };
  }

  // Handle Microsoft icons
  if (iconType === 'microsoft-icon') {
    const MicrosoftIcon = getMicrosoftIcon(iconName);
    if (MicrosoftIcon) {
      return {
        type: 'microsoft-icon',
        component: MicrosoftIcon,
      };
    }
    // Fallback to AlertCircle if Microsoft icon not found
    return {
      type: 'lucide',
      component: AlertCircle,
    };
  }

  if (iconType === 'simple-icon') {
    try {
      // Simple Icons use 'si' prefix (e.g., 'siSlack', 'siGithub')
      // But the package exports them with capital S (e.g., 'SiSlack', 'SiGithub')
      const capitalizedName = iconName.charAt(0).toUpperCase() + iconName.charAt(1).toLowerCase() + iconName.slice(2);
      const SimpleIcon = (SimpleIcons as any)[capitalizedName];
      const colorHex = (SimpleIcons as any)[`${capitalizedName}Hex`];

      if (!SimpleIcon) {
        return {
          type: 'lucide',
          component: AlertCircle,
        };
      }

      return {
        type: 'simple-icon',
        component: SimpleIcon,
        // Simple Icons provide hex colors as separate exports
        color: colorHex ? `#${colorHex}` : undefined,
      };
    } catch (error) {
      // console.error(`Error loading Simple Icon "${iconName}":`, error);
      return {
        type: 'lucide',
        component: AlertCircle,
      };
    }
  }

  // Default to Lucide icons
  if (lucideIconMap) {
    const lucideIcon = lucideIconMap[iconName];

    if (!lucideIcon) {
    }

    return {
      type: 'lucide',
      component: lucideIcon || AlertCircle,
    };
  }

  return {
    type: 'lucide',
    component: AlertCircle,
  };
}
